import { useTimetableSettings } from "@features/timetables/settingsQueries";
import { isTimetableVisible } from "@features/timetables/settings";
import { useStudentPersonaSuspense } from "@features/personas/queries";
import { Badge, Group, Stack, Text, ThemeIcon } from "@mantine/core";
import { useStudentExtendedPersonaSuspense } from "../queries";
import { IconLeafFilled, IconUser } from "@tabler/icons-react";
import { StudentPhotoSuspense } from "@features/personas/components/StudentPhoto";
import { CurrentLesson } from "@features/student-page/sub-features/student-current-lesson/components/CurrentLesson";
import { AttendanceBadge } from "@ui/AttendanceBadge";
import { LessonsToday } from "@features/student-page/sub-features/student-lessons-today/components/LessonsToday";
import { usePortalTabs } from "@features/portal-tabs/usePortalTabs";
import { StudentActionBar, useStudentActions, type StudentAction } from "@features/student-page/components/StudentActions";

export const getAttendanceColor = (value: number): string | undefined => {
    if (value >= 0.9) {
        return 'green';
    }
    if (value >= 0.85) {
        return 'orange';
    }
    return 'red';
};

export const StudentExtendedPersona: React.FC<{ studentNumber: string }> = ({ studentNumber }) => {

    const { data: timetableSettings } = useTimetableSettings();
    const lessonsVisible = Boolean(timetableSettings && isTimetableVisible(timetableSettings.visibility, timetableSettings.audience));
    const { data: data } = useStudentPersonaSuspense(studentNumber);
    const { data: extendedData } = useStudentExtendedPersonaSuspense(studentNumber);

    const { openStudent } = usePortalTabs();
    const studentActions = useStudentActions(studentNumber);
    const openFullProfileAction: StudentAction = {
        id: 'open-full-profile',
        label: 'Open full profile',
        icon: <IconUser size={16} />,
        inActionBar: true,
        onClick: () => openStudent(data.studentNumber),
    };

    return (
        <Stack gap={'xs'} miw={200}>
            <Stack gap={7}>
                <Group>
                    <div className={'size-10 overflow-hidden rounded-full'}>
                        <StudentPhotoSuspense id={studentNumber} />
                    </div>
                    <Stack gap={0}>
                        <Text size="md" fw={500} td={data.withdrawn ? 'line-through' : undefined}>{data.displayName}<Text c="dimmed" span> ({extendedData.pronouns})</Text></Text>
                        <Group gap={'xs'}>
                            <Text size="xs" c="dimmed">{data.studentNumber}</Text>
                        </Group>
                        
                        {data.withdrawn && <Badge size="xs" color="red" variant="light">Withdrawn</Badge>}
                    </Stack>
                </Group>
                <Group>
                    {extendedData.greenLeaf ? <ThemeIcon color="green" size={'xs'} radius={'sm'}><IconLeafFilled size={14} color="white"/></ThemeIcon> : null}
                    {extendedData.orangeLeaf ? <ThemeIcon color="orange" size={'xs'} radius={'sm'}><IconLeafFilled size={14} color="white"/></ThemeIcon> : null}
                    {extendedData.safeguardingTier > 0 ? <Badge color={extendedData.safeguardingTier === 1 ? 'yellow' : extendedData.safeguardingTier === 2 ? 'orange' : 'red'} variant="light" size="xs" tt={'none'}>Tier {extendedData.safeguardingTier}</Badge> : null}
                </Group>
            </Stack>
            <StudentActionBar actions={[openFullProfileAction, ...studentActions]} px={0} pb={0} />
            <Group>
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">Attendance</Text>
                    <AttendanceBadge size="md" attendance={extendedData.overallPresentLength && extendedData.overallPresentTotal ? extendedData.overallPresentLength / extendedData.overallPresentTotal : 0} />
                </Stack>
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">Punctuality</Text>
                    <AttendanceBadge size="md" attendance={extendedData.punctLength && extendedData.totalPunctLength ? extendedData.punctLength / extendedData.totalPunctLength : 0} />
                </Stack>
            </Group>
            <div>
                {lessonsVisible ? <CurrentLesson lesson={extendedData.whereIsStudentRightNow ?? null} /> : <Text size="xs" c="dimmed">Timetables are not yet available</Text>}
            </div>
            <div>
                {lessonsVisible && <LessonsToday lessons={extendedData.lessonsToday ?? null} />}
            </div>
            {/* <Card p={0} radius={'md'} className={'shadow-none!'} withBorder>
                <Table variant="vertical">
                    <Table.Tbody>
                        {extendedData.overallPresentLength && extendedData.overallPresentTotal ? 
                        <Table.Tr>
                            <Table.Th className="w-20"><Text size="xs">Attendance</Text></Table.Th>
                            <Table.Td className={'w-16'}><Text c={getAttendanceColor(extendedData.overallPresentLength / extendedData.overallPresentTotal)} size="xs">{renderAttendance(extendedData.overallPresentLength, extendedData.overallPresentTotal)}</Text></Table.Td>
                        </Table.Tr> : null}

                        {extendedData.punctLength && extendedData.totalPunctLength ? <Table.Tr>
                            <Table.Th className="w-20"><Text size="xs">Punctuality</Text></Table.Th>
                            <Table.Td className={'w-16'}><Text c={getAttendanceColor(extendedData.punctLength / extendedData.totalPunctLength)} size="xs">{renderPunctuality(extendedData.punctLength, extendedData.totalPunctLength)}</Text></Table.Td>
                        </Table.Tr> : null}
                    </Table.Tbody>
                </Table>
            </Card> */}
        </Stack>
    )
}
